import type React from 'react';
import { useCallback } from 'react';

import { cn } from '../../../utils/cn';

export type ContentSwitcherOption<T extends string = string> = {
  value: T;
  label?: string;
  icon?: React.ReactNode;
  ariaLabel: string;
};

export type ContentSwitcherProps<T extends string = string> = {
  options: ContentSwitcherOption<T>[];
  value: T;
  onChange: (value: T) => void;
  inverse?: boolean;
  size?: 'regular' | 'small';
  disabled?: boolean;
  ariaLabel?: string;
};

function ContentSwitcher<T extends string = string>({
  options,
  value,
  onChange,
  inverse = false,
  size = 'regular',
  disabled = false,
  ariaLabel = 'Content switcher',
}: ContentSwitcherProps<T>) {
  const handleSelect = useCallback(
    (optionValue: T) => {
      if (!disabled && optionValue !== value) {
        onChange(optionValue);
      }
    },
    [disabled, value, onChange],
  );

  return (
    <div
      className="overflow-clip flex flex-col items-start p-xxs2 rounded-full"
      aria-label={ariaLabel}
      role="group"
    >
      <div
        className={cn(
          'overflow-clip flex gap-xs1 items-center w-full border-xxs2 border-solid rounded-full',
          inverse
            ? 'bg-background-accent-inverse border-background-accent-inverse'
            : 'bg-[var(--tw-grey-10)] border-[var(--tw-grey-10)]',
        )}
      >
        {options.map(option => {
          const isSelected = option.value === value;

          return (
            <button
              key={option.value}
              aria-label={option.ariaLabel}
              aria-pressed={isSelected}
              disabled={disabled}
              type="button"
              className={cn(
                'cursor-pointer flex flex-shrink-0 gap-xs2 items-center justify-center border-none rounded-full',
                'transition-[background-color,color] duration-[150ms] ease-in-out',
                'focus-visible:[outline:2px_solid_var(--tw-border-focus)] focus-visible:outline-offset-2',
                'disabled:cursor-default disabled:opacity-50',
                size === 'small' ? 'p-xs1' : 'p-xs2',
                isSelected
                  ? inverse
                    ? 'text-primary-inverse bg-background-primary'
                    : 'text-primary bg-background'
                  : inverse
                    ? 'text-icon-secondary-inverse bg-background-accent-inverse [&:hover:not(:disabled)]:text-primary-inverse [&:hover:not(:disabled)]:bg-[var(--tw-background-inverse-hover)]'
                    : 'text-primary bg-[var(--tw-grey-10)] [&:hover:not(:disabled)]:bg-[var(--tw-grey-05)]',
              )}
              onClick={() => handleSelect(option.value)}
            >
              {option.icon}
              {option.label && <span>{option.label}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ContentSwitcher;
